/**
 * SoloLeaderboardPanel — the leaderboard below the solo race track.
 *
 * Filters (top → bottom, per plan):
 *   1. Duration  — Today / This Week / All-time
 *   2. Metric    — Points / Score
 *   3. Time ctrl — 30/60/90/120/180/300s, DEFAULTS to the current game's timerSec
 *                  (seeded by the caller), switchable to browse other boards.
 *
 * Rows: Top 5 + a pinned "You" row when you're outside the top 5 (its true rank
 * kept). Mirrors the dashboard leaderboard's shape but self-contained (inline
 * rank badge + initials avatar) so it stays DECOUPLED from the dashboard screen's
 * theme/VM. Rows slide on reorder (RN-for-Web `top` transition, like the race
 * strip). Icons only — no emojis.
 *
 * Freshness: the query auto-refetches (30s) for other players; YOUR just-finished
 * score is made instant by invalidate-on-end in PuzzleRoyaleBattle (SL-7).
 */

import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

import useSoloLeaderboard, {
  type SoloDuration,
  type SoloMetric,
  type SoloLeaderboardEntry,
} from '@/Hooks/FunChallengesApi/useSoloLeaderboard';
import {TIME_CHIPS} from '@/Screens/FunChallenges/royaleConstants';
// Reuse the SAME podium palette + avatar colours as the dashboard leaderboard so
// the two boards read as one system (item 4). MEDAL = gold/silver/bronze bg+ink.
import {
  MEDAL,
  avatarColorForUser,
} from '@/Screens/Leaderboard/leaderboardTheme';

const BROWN = CCColors.Primary?.Brown ?? '#4D3B22';
const GREY_TEXT = '#8A8677';
const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const CARD_BG = CCColors.White ?? '#FFFFFF';
const BORDER = 'rgba(38, 35, 34, 0.10)';
const YOU_TINT = 'rgba(61, 171, 158, 0.10)';

const DURATIONS: Array<{key: SoloDuration; label: string}> = [
  {key: 'daily', label: 'Today'},
  {key: 'weekly', label: 'Week'},
  {key: 'alltime', label: 'All-time'},
];
const METRICS: Array<{key: SoloMetric; label: string}> = [
  {key: 'points', label: 'Points'},
  {key: 'score', label: 'Score'},
];

interface Props {
  /** The current game's time control — the DEFAULT board shown. */
  defaultTimeControlSec: number;
  /**
   * True once the player has finished a game this session — enables
   * refetch-on-focus so a tab-away/return catches up on other players. No
   * background poll (see useSoloLeaderboard header).
   */
  hasEndedThisSession?: boolean;
}

export default function SoloLeaderboardPanel({
  defaultTimeControlSec,
  hasEndedThisSession = false,
}: Props) {
  const [duration, setDuration] = useState<SoloDuration>('daily');
  const [metric, setMetric] = useState<SoloMetric>('points');
  const [timeControl, setTimeControl] = useState<number>(defaultTimeControlSec);

  const {snapshot, isLoading} = useSoloLeaderboard(
    duration,
    metric,
    timeControl,
    hasEndedThisSession,
  );

  const entries = snapshot?.entries ?? [];
  const me = snapshot?.me ?? null;
  // ALWAYS pin a "You" row at the bottom (even when you're in the top 5) so this
  // board's viewport matches the other screens — a fixed "you" anchor at the
  // foot (item 5). Only omitted when you have no rank at all (never played this
  // board's time control).
  const pinnedYou =
    me && me.rank != null
      ? {
          rank: me.rank,
          userid: me.userid,
          name: me.name,
          avatarUrl: me.avatarUrl,
          value: me.value,
          you: true,
        }
      : null;

  return (
    <View style={styles.root}>
      <View style={styles.titleRow}>
        <CCText style={styles.title}>Leaderboard</CCText>
        {!!snapshot?.totalPlayers && (
          <CCText style={styles.titleSub}>
            {snapshot.totalPlayers} player{snapshot.totalPlayers === 1 ? '' : 's'}
          </CCText>
        )}
      </View>

      {/* 1. Duration */}
      <SegRow
        options={DURATIONS}
        value={duration}
        onChange={setDuration}
        variant="teal"
      />
      {/* 2. Metric */}
      <SegRow
        options={METRICS}
        value={metric}
        onChange={setMetric}
        variant="brown"
      />
      {/* 3. Time control — compact chips; defaults to the current game's. */}
      <View style={styles.tcRow}>
        {TIME_CHIPS.map(tc => {
          const active = timeControl === tc;
          return (
            <Pressable
              key={tc}
              onPress={() => setTimeControl(tc)}
              style={[styles.tcChip, active && styles.tcChipActive]}>
              <CCText
                style={[styles.tcChipText, active && styles.tcChipTextActive]}>
                {tc}s
              </CCText>
            </Pressable>
          );
        })}
      </View>

      {/* Rows */}
      <View style={styles.rows}>
        {isLoading && entries.length === 0 ? (
          <CCText style={styles.empty}>Loading…</CCText>
        ) : entries.length === 0 ? (
          <CCText style={styles.empty}>
            No games yet at {timeControl}s. Be the first!
          </CCText>
        ) : (
          entries.map(e => (
            // In the ranked list, show your REAL name (not "You") — the canonical
            // "You" is the pinned bottom row (item 5), so a top-5 self row reads
            // as your name in the standings, avoiding a confusing double "You".
            <Row key={e.userid} entry={{...e, you: false}} metric={metric} />
          ))
        )}
        {pinnedYou && (
          <>
            <View style={styles.pinDivider} />
            <Row entry={pinnedYou} metric={metric} />
          </>
        )}
      </View>
    </View>
  );
}

function Row({
  entry,
  metric,
}: {
  entry: SoloLeaderboardEntry;
  metric: SoloMetric;
}) {
  // Podium palette shared with the dashboard leaderboard (item 4): gold/silver/
  // bronze filled badge with matching ink for ranks 1-3; a plain outlined chip
  // for the rest so the top three visibly stand out.
  const medal = MEDAL[entry.rank] ?? null;
  const initials = (entry.name || 'P').trim().slice(0, 2).toUpperCase();
  const avatarBg = avatarColorForUser(entry.userid);
  return (
    <View style={[styles.row, entry.you && styles.rowYou]}>
      <View
        style={[
          styles.rankBadge,
          medal ? {backgroundColor: medal.bg} : styles.rankBadgePlain,
        ]}>
        <CCText
          style={[
            styles.rankText,
            medal ? {color: medal.tx} : null,
          ]}>
          {entry.rank}
        </CCText>
      </View>
      <View style={[styles.avatar, {backgroundColor: avatarBg}]}>
        <CCText style={styles.avatarText}>{initials}</CCText>
      </View>
      <CCText style={[styles.name, entry.you && styles.nameYou]} numberOfLines={1}>
        {entry.you ? 'You' : entry.name || 'Player'}
      </CCText>
      <CCText style={styles.value}>{entry.value}</CCText>
    </View>
  );
}

function SegRow<T extends string | number>({
  options,
  value,
  onChange,
  variant,
}: {
  options: Array<{key: T; label: string}>;
  value: T;
  onChange: (v: T) => void;
  variant: 'teal' | 'brown';
}) {
  const activeStyle = variant === 'teal' ? styles.segActiveTeal : styles.segActiveBrown;
  return (
    <View style={styles.segRow}>
      {options.map(o => {
        const active = value === o.key;
        return (
          <Pressable
            key={String(o.key)}
            onPress={() => onChange(o.key)}
            style={[styles.seg, active && activeStyle]}>
            <CCText style={[styles.segText, active && styles.segTextActive]}>
              {o.label}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  // White card surface so the panel lifts off the cream page (BG_PAGE #FAF6EB) —
  // without it the rows blend into the background on the warm board theme.
  root: {
    width: '100%',
    gap: 8,
    backgroundColor: CARD_BG,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    padding: 14,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '800',
    color: BROWN,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  titleSub: {fontSize: FontSizes[12] || 12, color: '#221F1E', fontWeight: '800'},
  segRow: {
    flexDirection: 'row',
    gap: 4,
    backgroundColor: '#F4EEDB',
    borderRadius: 10,
    padding: 3,
  },
  seg: {flex: 1, paddingVertical: 7, borderRadius: 8, alignItems: 'center'},
  segActiveTeal: {backgroundColor: TEAL},
  segActiveBrown: {backgroundColor: BROWN},
  segText: {fontSize: FontSizes[12] || 12, fontWeight: '700', color: GREY_TEXT},
  segTextActive: {color: CCColors.White ?? '#fff'},
  tcRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 5},
  tcChip: {
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CARD_BG,
  },
  tcChipActive: {backgroundColor: TEAL_DARK, borderColor: TEAL_DARK},
  tcChipText: {fontSize: FontSizes[11] || 11, fontWeight: '700', color: GREY_TEXT},
  tcChipTextActive: {color: CCColors.White ?? '#fff'},
  rows: {gap: 4, marginTop: 2},
  empty: {
    fontSize: FontSizes[12] || 12,
    color: GREY_TEXT,
    textAlign: 'center',
    paddingVertical: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
    // A faint fill so each row separates from the white card + from each other.
    backgroundColor: '#FAF6EB',
    // RN-for-Web smooth reorder when rank changes.
    transition: 'background-color 0.3s ease' as any,
  },
  rowYou: {
    backgroundColor: YOU_TINT,
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.45)',
  },
  rankBadge: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Ranks 4+: an outlined chip (no fill) so only the podium is coloured.
  rankBadgePlain: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: BORDER,
  },
  rankText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: GREY_TEXT},
  avatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#E7DFC8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {fontSize: FontSizes[11] || 11, fontWeight: '800', color: '#fff'},
  name: {flex: 1, fontSize: FontSizes[13] || 13, fontWeight: '600', color: BROWN},
  nameYou: {fontWeight: '800', color: TEAL_DARK},
  value: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '800',
    color: BROWN,
    minWidth: 34,
    textAlign: 'right',
  },
  pinDivider: {
    height: 1,
    backgroundColor: BORDER,
    marginVertical: 3,
    marginHorizontal: 4,
  },
});
